// ============================================================
// ChartView — TradingView Lightweight Charts: Candles + Volume,
// REST-Backfill → nahtlose Live-Updates, Crosshair-Readout (UTC+2),
// und ein PIXELGENAU synchronisierter <canvas>-Overlay für TPO,
// Indikator-Linien und (Phase 5) Zeichen-Tools.
// ============================================================
import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import {
  createChart, IChartApi, ISeriesApi, CandlestickData, HistogramData,
  UTCTimestamp, CrosshairMode, ColorType, TickMarkType,
} from 'lightweight-charts';
import { Bar, KlineMsg, fmtTimeUTC2, fmtDateUTC2, fmtPrice, TIMEFRAMES } from './types';
import { TpoProfile, letterFor } from './tpoEngine';

const API = '/api';

export interface OverlayShow {
  tpo: boolean;
  mode: 'letter' | 'profile';
  ib: boolean;
  singles: boolean;
  excess: boolean;
  prevLevels: boolean;   // pVAH/pPOC/pVAL
  devLevels: boolean;    // developing POC/VAH/VAL
}

export interface OverlayState {
  today: TpoProfile | null;
  prev: TpoProfile | null;
  bracketMin: number;
  show: OverlayShow;
  // Phase 4: ATR-Hilfslinien
  atrLines?: { label: string; price: number; color: string }[];
}

export type DrawTool = 'none' | 'trend' | 'rect' | 'hline' | 'measure' | 'zoom' | 'long' | 'short';

interface Pt { t: number; p: number; } // Zeit (s) + Preis — Daten-Koordinaten
interface Drawing {
  id: number;
  kind: DrawTool;
  a: Pt;
  b?: Pt; // bei hline nur `a` (Preis)
}

// Long/Short-Position-Tool (Block C): preisgebundene Levels + Zeitspanne der Box.
export interface Position {
  id: number;
  side: 'long' | 'short';
  entry: number; stop: number; target: number;
  t0: number; t1: number; // Box-Zeitspanne (Sekunden)
}

// Was „Als Trade speichern“ ans Trade-Formular übergibt.
export interface PositionTrade {
  side: 'long' | 'short';
  symbol: string;
  entry: number; stop: number; target: number;
  rr: number; timeSec: number;
}

export interface ChartHandle {
  applyKline: (k: KlineMsg) => void;
  getChart: () => IChartApi | null;
  getCandleSeries: () => ISeriesApi<'Candlestick'> | null;
  redrawOverlay: () => void;
  clearDrawings: () => void;
  undoDrawing: () => void;
  deleteSelected: () => void;
  fitDay: () => void;
  autoScale: () => void;
  priceAtY: (clientY: number) => number | null;
  snapshot: () => string | null;
  getSelectedPosition: () => PositionTrade | null;
}

interface Props {
  symbol: string;
  tfCode: string;
  tickSize: number;
  overlay?: OverlayState;
  tool?: DrawTool;
  magnet?: boolean;
  posQty?: number;            // Block C: Menge für die P&L-Rechnung des Position-Tools
  onPositionChange?: (hasSelection: boolean) => void; // meldet Auswahl an die Toolbar
  onReady?: () => void;
  onBars?: (bars: Bar[], confirmedTick: boolean) => void;
  // Rechtsklick (Block D): meldet Cursor-Position + Preis/Zeit an die Seite.
  onContext?: (info: { clientX: number; clientY: number; price: number | null; time: number | null }) => void;
}

interface HoverInfo { o: number; h: number; l: number; c: number; v: number; time: number; }

const COL = {
  poc: '#f59e0b', va: '#22d3ee', out: '#5b6478', single: '#c084fc', excess: '#fb923c',
  vah: '#22c55e', val: '#ef4444', ib: '#3b82f6', prev: '#94a3b8',
};

const ChartView = forwardRef<ChartHandle, Props>(({ symbol, tfCode, tickSize, overlay, tool = 'none', magnet = false, posQty = 1, onPositionChange, onReady, onBars, onContext }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const readyRef = useRef(false);
  const lastTimeRef = useRef(0);
  const barsRef = useRef<Map<number, Bar>>(new Map());
  const overlayRef = useRef<OverlayState | undefined>(overlay);
  const tickRef = useRef(tickSize);
  tickRef.current = tickSize;
  const rafRef = useRef<number | null>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [lastBar, setLastBar] = useState<HoverInfo | null>(null);

  // --- Zeichen-Tools (pro Symbol, localStorage) ---
  const drawingsRef = useRef<Drawing[]>([]);
  const draftRef = useRef<Drawing | null>(null);
  const toolRef = useRef<DrawTool>(tool);
  toolRef.current = tool;
  const magnetRef = useRef(magnet);
  magnetRef.current = magnet;
  const selectedIdRef = useRef<number | null>(null);
  const pinnedTimeRef = useRef<number | null>(null);
  const measureRef = useRef<{ a: Pt; b: Pt } | null>(null);
  const lsKey = `tpo_drawings_${symbol}`;
  const tfSec = TIMEFRAMES.find(t => t.code === tfCode)?.sec ?? 1800;

  // --- Long/Short-Positionen (Block C, pro Symbol in localStorage) ---
  const positionsRef = useRef<Position[]>([]);
  const posSelectedRef = useRef<number | null>(null);
  const posDragRef = useRef<{ id: number; part: 'entry' | 'stop' | 'target'; grabPrice: number; entry0: number; stop0: number; target0: number } | null>(null);
  const posLsKey = `tpo_positions_${symbol}`;
  const posQtyRef = useRef(posQty);
  posQtyRef.current = posQty;
  const persistPos = () => { try { localStorage.setItem(posLsKey, JSON.stringify(positionsRef.current)); } catch {} };
  const notifySel = () => { onPositionChange?.(posSelectedRef.current != null); };

  const persist = () => { try { localStorage.setItem(lsKey, JSON.stringify(drawingsRef.current)); } catch {} };

  overlayRef.current = overlay;

  // ---- Overlay-Zeichnen (an Chart-Koordinaten gekoppelt) ----
  const draw = () => {
    const canvas = canvasRef.current, chart = chartRef.current, series = candleRef.current, el = containerRef.current;
    if (!canvas || !chart || !series || !el) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = el.clientWidth, H = el.clientHeight;
    // Auf schmalen Screens (Phone) Labels kürzen + stärker ausdünnen (Block E).
    const isNarrow = W < 768;
    if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const ov = overlayRef.current;
    if (!ov) return;
    const ts = chart.timeScale();
    const plotW = ts.width();
    const x = (sec: number) => ts.timeToCoordinate(sec as UTCTimestamp);
    const y = (price: number) => series.priceToCoordinate(price);

    // ---- Label-Kollisionsvermeidung (Block E) ----
    // Linien werden sofort gezeichnet; Labels gesammelt und am Ende kollisionsfrei
    // platziert: hohe Priorität (POC/VAH/VAL) zuerst, überlappende niedrigere werden
    // genudged und auf Phone notfalls ausgedünnt. Auf Phone: nur Kürzel, keine Werte.
    type LabelJob = { y: number; text: string; color: string; x0: number; prio: number };
    const labelJobs: LabelJob[] = [];

    // gestrichelte horizontale Linie + (gequeuetes) Label
    const hline = (price: number, color: string, label: string, dash = false, x0 = 0, prio = 5, short?: string) => {
      const yy = y(price); if (yy == null) return;
      ctx.strokeStyle = color; ctx.lineWidth = 1;
      ctx.setLineDash(dash ? [5, 4] : []);
      ctx.beginPath(); ctx.moveTo(x0, yy); ctx.lineTo(plotW, yy); ctx.stroke();
      ctx.setLineDash([]);
      labelJobs.push({ y: yy - 3, text: isNarrow ? (short ?? label) : label, color, x0, prio });
    };

    const flushLabels = () => {
      const LABEL_H = 11;
      const placed: number[] = [];
      const collides = (yy: number) => placed.some(p => Math.abs(yy - p) < LABEL_H);
      // Wichtigste zuerst (höhere prio), bei Gleichstand von oben nach unten.
      labelJobs.sort((a, b) => b.prio - a.prio || a.y - b.y);
      ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      for (const job of labelJobs) {
        let ly = job.y;
        if (collides(ly)) {
          let found = false;
          for (let step = 1; step <= 6 && !found; step++) {
            for (const cand of [ly + step * LABEL_H, ly - step * LABEL_H]) {
              if (cand > 8 && cand < H - 2 && !collides(cand)) { ly = cand; found = true; break; }
            }
          }
          // Phone: lässt sich kein Platz finden → niedrigprioritäres Label ausdünnen.
          if (!found && isNarrow) continue;
        }
        placed.push(ly);
        ctx.fillStyle = job.color;
        ctx.fillText(job.text, job.x0 + 4, ly);
      }
    };

    // ---- Vortags-Linien (pVAH/pPOC/pVAL) — sekundär (prio 3/4) ----
    if (ov.prev && ov.show.prevLevels) {
      hline(ov.prev.vah, COL.prev, 'pVAH ' + fmtPrice(ov.prev.vah, tickRef.current), true, 0, 3, 'pVAH');
      hline(ov.prev.poc, COL.poc, 'pPOC ' + fmtPrice(ov.prev.poc, tickRef.current), true, 0, 4, 'pPOC');
      hline(ov.prev.val, COL.prev, 'pVAL ' + fmtPrice(ov.prev.val, tickRef.current), true, 0, 3, 'pVAL');
    }

    // Profil-Kern (Letter/Histogramm) — für Vortag UND heute identisch, nur räumlich getrennt.
    const drawCore = (p: TpoProfile) => {
      if (!p.rows.length) return;
      const xDayStart = x(p.dayStartSec);
      const dayX0 = xDayStart != null ? Math.max(0, xDayStart) : 0;
      const yA = y(p.rows[0].price), yB = y(p.rows[0].price + p.rowSize);
      const rowH = yA != null && yB != null ? Math.abs(yA - yB) : 6;
      const fontPx = Math.max(7, Math.min(11, rowH * 0.95));
      const colorOf = (r: TpoProfile['rows'][number]) =>
        r.excess && ov.show.excess ? COL.excess
          : r.isPoc ? COL.poc
          : r.singlePrint && ov.show.singles ? COL.single
          : r.inVa ? COL.va : COL.out;

      // Lesbarkeits-Fallback: bei zu geringer Reihenhöhe (Rauszoomen) IMMER auf
      // Balken zurückfallen, sonst wird das Letter-Profil zur Pixelsuppe.
      const useBars = ov.show.mode === 'profile' || rowH < 8;

      if (useBars) {
        const maxCount = p.rows.reduce((m, r) => Math.max(m, r.count), 1);
        const PROFILE_W = Math.min(170, plotW * 0.32);
        for (const r of p.rows) {
          const yy = y(r.price + p.rowSize / 2); if (yy == null) continue;
          const h = Math.max(1, rowH - 1);
          ctx.globalAlpha = r.inVa || r.isPoc ? 0.55 : 0.35;
          ctx.fillStyle = colorOf(r);
          ctx.fillRect(dayX0, yy - h / 2, (r.count / maxCount) * PROFILE_W, h);
          ctx.globalAlpha = 1;
        }
      } else {
        // ---- Echtes Market-Profile-Letter-Profil ----
        // Buchstaben am linken Rand der Tages-Region gestapelt; X = Letter-Reihenfolge
        // (Spaltenindex innerhalb der Reihe), NICHT Uhrzeit. Längste Reihe = POC →
        // die Form bildet die Glocke (ATAS-Stil). Y preisgebunden via priceToCoordinate.
        const cellW = Math.max(5, fontPx * 0.62); // Monospace-Glyphenbreite
        const maxRight = plotW + 5;

        // POC-Reihe dezent hinterlegen (Betonung).
        const pocRow = p.rows.find(r => r.isPoc);
        if (pocRow) {
          const yy = y(pocRow.price + p.rowSize / 2);
          if (yy != null) {
            ctx.fillStyle = '#f59e0b1f';
            ctx.fillRect(dayX0, yy - rowH / 2, Math.min(maxRight - dayX0, pocRow.count * cellW), rowH);
          }
        }

        ctx.font = `${fontPx}px monospace`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        for (const r of p.rows) {
          const yy = y(r.price + p.rowSize / 2); if (yy == null || yy < -10 || yy > H + 10) continue;
          // Value-Area-Reihen leicht hervorheben (höhere Deckkraft als Außenbereich).
          ctx.globalAlpha = r.isPoc || r.inVa ? 1 : 0.75;
          ctx.fillStyle = colorOf(r);
          for (let i = 0; i < r.brackets.length; i++) {
            const xx = dayX0 + i * cellW + cellW / 2;
            if (xx > maxRight) break; // rechts aus dem Bild → Rest abschneiden
            ctx.fillText(letterFor(r.brackets[i]), xx, yy);
          }
        }
        ctx.globalAlpha = 1;
        ctx.textBaseline = 'alphabetic';
      }
    };

    if (ov.show.tpo) {
      // Vortags-Profil in seiner Zeitregion (links der Tagesgrenze), gleiche Farben.
      if (ov.prev) drawCore(ov.prev);

      const p = ov.today;
      if (p && p.rows.length) {
        drawCore(p);
        const xDayStart = x(p.dayStartSec);
        const dayX0 = xDayStart != null ? Math.max(0, xDayStart) : 0;

        // Vertikale Tagesgrenz-Linie bei 00:00 UTC
        if (xDayStart != null && xDayStart >= 0 && xDayStart <= plotW) {
          ctx.strokeStyle = '#475569'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
          ctx.beginPath(); ctx.moveTo(xDayStart, 0); ctx.lineTo(xDayStart, H); ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#64748b'; ctx.font = '9px sans-serif'; ctx.textAlign = 'center';
          ctx.fillText('00:00 UTC', xDayStart, 12);
        }

        // ST/BT-Excess-Labels
        if (ov.show.excess) {
          const top = [...p.rows].reverse().find(r => r.excess);
          const bot = p.rows.find(r => r.excess);
          if (top) { const yy = y(top.price + p.rowSize / 2); if (yy != null) { ctx.fillStyle = COL.excess; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left'; ctx.fillText('ST', dayX0 + 2, yy - 2); } }
          if (bot && bot !== top) { const yy = y(bot.price + p.rowSize / 2); if (yy != null) { ctx.fillStyle = COL.excess; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left'; ctx.fillText('BT', dayX0 + 2, yy + 9); } }
        }

        if (ov.show.devLevels) {
          // Primär-Linien — höchste Priorität (bevorzugt sichtbar auf Phone).
          hline(p.poc, COL.poc, 'POC ' + fmtPrice(p.poc, tickRef.current), false, dayX0, 10, 'POC');
          hline(p.vah, COL.vah, 'VAH ' + fmtPrice(p.vah, tickRef.current), false, dayX0, 9, 'VAH');
          hline(p.val, COL.val, 'VAL ' + fmtPrice(p.val, tickRef.current), false, dayX0, 9, 'VAL');
        }
        if (ov.show.ib && p.ibHigh > 0) {
          hline(p.ibHigh, COL.ib, 'IB-H', true, dayX0, 5, 'IB-H');
          hline(p.ibLow, COL.ib, 'IB-L', true, dayX0, 5, 'IB-L');
        }
      }
    }

    // ---- ATR-Hilfslinien (Phase 4) — niedrige Priorität ----
    if (ov.atrLines) {
      for (const l of ov.atrLines) {
        const short = l.label.replace('×ATR', '').replace(/\s+/g, '');
        hline(l.price, l.color, l.label, true, 0, 2, short);
      }
    }

    // Gesammelte Labels jetzt kollisionsfrei zeichnen (Block E).
    flushLabels();

    // ---- Fixierte vertikale Cursorlinie (Block F) ----
    if (pinnedTimeRef.current != null) {
      const xp = x(pinnedTimeRef.current);
      if (xp != null && xp >= 0 && xp <= plotW) {
        ctx.strokeStyle = '#8b5cf6'; ctx.lineWidth = 1; ctx.setLineDash([2, 3]);
        ctx.beginPath(); ctx.moveTo(xp, 0); ctx.lineTo(xp, H); ctx.stroke(); ctx.setLineDash([]);
      }
    }

    // ---- Zeichen-Tools (Phase 5) — in Daten-Koordinaten, zoom-fest ----
    const drawOne = (d: Drawing, selected: boolean) => {
      const accent = selected ? '#ffffff' : null;
      ctx.lineWidth = selected ? 2.2 : 1.4; ctx.setLineDash([]);
      if (d.kind === 'hline') {
        const yy = y(d.a.p); if (yy == null) return;
        ctx.strokeStyle = accent ?? '#eab308';
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(plotW, yy); ctx.stroke();
        ctx.fillStyle = '#eab308'; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left';
        ctx.fillText(fmtPrice(d.a.p, tickRef.current), 4, yy - 3);
        return;
      }
      if (!d.b) return;
      const x1 = x(d.a.t), y1 = y(d.a.p), x2 = x(d.b.t), y2 = y(d.b.p);
      if (x1 == null || y1 == null || x2 == null || y2 == null) return;
      ctx.strokeStyle = accent ?? '#38bdf8';
      if (d.kind === 'trend') { ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }
      else if (d.kind === 'rect') {
        const rx = Math.min(x1, x2), ry = Math.min(y1, y2), rw = Math.abs(x2 - x1), rh = Math.abs(y2 - y1);
        ctx.fillStyle = '#38bdf822'; ctx.fillRect(rx, ry, rw, rh); ctx.strokeRect(rx, ry, rw, rh);
      }
    };
    for (const d of drawingsRef.current) drawOne(d, d.id === selectedIdRef.current);
    if (draftRef.current) drawOne(draftRef.current, false);

    // ---- Long/Short-Positionen (Block C) ----
    const drawPosition = (pos: Position, selected: boolean) => {
      const yE = y(pos.entry), yS = y(pos.stop), yT = y(pos.target);
      if (yE == null || yS == null || yT == null) return;
      const xLraw = x(pos.t0), xRraw = x(pos.t1);
      const left = Math.max(0, Math.min(xLraw ?? 0, xRraw ?? plotW));
      const right = Math.min(plotW, Math.max(xLraw ?? 0, xRraw ?? plotW));
      const w = Math.max(40, right - left);
      // Risiko-Zone (Entry→Stop) rot, Profit-Zone (Entry→Target) grün.
      ctx.fillStyle = '#ef444422'; ctx.fillRect(left, Math.min(yE, yS), w, Math.abs(yS - yE));
      ctx.fillStyle = '#22c55e22'; ctx.fillRect(left, Math.min(yE, yT), w, Math.abs(yT - yE));
      const lineAt = (yy: number, col: string) => { ctx.strokeStyle = col; ctx.lineWidth = selected ? 2 : 1.2; ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(left, yy); ctx.lineTo(right, yy); ctx.stroke(); };
      lineAt(yE, '#e2e8f0'); lineAt(yS, '#ef4444'); lineAt(yT, '#22c55e');
      const handle = (yy: number, col: string) => { ctx.fillStyle = col; ctx.fillRect(right - 4, yy - 4, 8, 8); };
      handle(yE, '#e2e8f0'); handle(yS, '#ef4444'); handle(yT, '#22c55e');

      // Live-Stats: R:R, Risiko/Reward in % und Preis, P&L aus konfigurierbarer Menge.
      const tick = tickRef.current;
      const qty = posQtyRef.current || 1;
      const risk = Math.abs(pos.entry - pos.stop), reward = Math.abs(pos.target - pos.entry);
      const rr = risk > 0 ? reward / risk : 0;
      const riskPct = pos.entry ? Math.abs((pos.entry - pos.stop) / pos.entry * 100) : 0;
      const rewardPct = pos.entry ? Math.abs((pos.target - pos.entry) / pos.entry * 100) : 0;
      const lines = [
        `${pos.side.toUpperCase()}  R:R ${rr.toFixed(2)}`,
        `Entry ${fmtPrice(pos.entry, tick)}`,
        `Risk  -${fmtPrice(risk * qty, tick)} (${riskPct.toFixed(2)}%)`,
        `Rew   +${fmtPrice(reward * qty, tick)} (${rewardPct.toFixed(2)}%)`,
      ];
      const bw = 158, bh = lines.length * 13 + 8;
      let bx = right + 6; if (bx + bw > plotW) bx = left - bw - 6; if (bx < 0) bx = left + 4;
      const by = Math.max(0, Math.min(yT, yS));
      ctx.fillStyle = selected ? '#161d33f2' : '#0c1120d8';
      ctx.strokeStyle = pos.side === 'long' ? '#22c55e' : '#ef4444'; ctx.lineWidth = 1; ctx.setLineDash([]);
      ctx.fillRect(bx, by, bw, bh); ctx.strokeRect(bx, by, bw, bh);
      ctx.font = '10px monospace'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      lines.forEach((ln, i) => { ctx.fillStyle = i === 0 ? (pos.side === 'long' ? '#22c55e' : '#ef4444') : '#cbd5e1'; ctx.fillText(ln, bx + 6, by + 11 + i * 13); });
      ctx.textBaseline = 'alphabetic';
    };
    for (const pos of positionsRef.current) drawPosition(pos, pos.id === posSelectedRef.current);

    // Aufzieh-Draft für Long/Short bzw. die Zoom-Box.
    const dr2 = draftRef.current;
    if (dr2 && dr2.b && (dr2.kind === 'long' || dr2.kind === 'short' || dr2.kind === 'zoom')) {
      const ya = y(dr2.a.p), yb = y(dr2.b.p), x1 = x(dr2.a.t), x2 = x(dr2.b.t);
      if (ya != null && yb != null) {
        const left = Math.min(x1 ?? 0, x2 ?? plotW), w = Math.abs((x2 ?? plotW) - (x1 ?? 0)) || 40;
        const col = dr2.kind === 'zoom' ? '#8b5cf6' : dr2.kind === 'long' ? '#22c55e' : '#ef4444';
        ctx.fillStyle = col + '22'; ctx.fillRect(left, Math.min(ya, yb), w, Math.abs(yb - ya));
        ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([4, 3]); ctx.strokeRect(left, Math.min(ya, yb), w, Math.abs(yb - ya)); ctx.setLineDash([]);
      }
    }

    // ---- Measure-Tool (Block G) ----
    const m = (draftRef.current && draftRef.current.kind === 'measure' && draftRef.current.b)
      ? { a: draftRef.current.a, b: draftRef.current.b } : measureRef.current;
    if (m) {
      const x1 = x(m.a.t), y1 = y(m.a.p), x2 = x(m.b.t), y2 = y(m.b.p);
      if (x1 != null && y1 != null && x2 != null && y2 != null) {
        const up = m.b.p >= m.a.p;
        const col = up ? '#22c55e' : '#ef4444';
        const rx = Math.min(x1, x2), ry = Math.min(y1, y2), rw = Math.abs(x2 - x1), rh = Math.abs(y2 - y1);
        ctx.fillStyle = up ? '#22c55e22' : '#ef444422'; ctx.fillRect(rx, ry, rw, rh);
        ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([4, 3]); ctx.strokeRect(rx, ry, rw, rh); ctx.setLineDash([]);
        const dP = m.b.p - m.a.p;
        const pct = m.a.p !== 0 ? (dP / m.a.p) * 100 : 0;
        const bars = Math.round((m.b.t - m.a.t) / tfSec);
        const lines = [
          `${dP >= 0 ? '+' : ''}${fmtPrice(dP, tickRef.current)} (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)`,
          `${Math.abs(bars)} bars`,
        ];
        const bw = 132, bh = 32, bx = Math.min(x1, x2) + rw / 2 - bw / 2, by = Math.min(y1, y2) - bh - 4;
        ctx.fillStyle = col; ctx.fillRect(bx, by, bw, bh);
        ctx.fillStyle = '#0a0e1a'; ctx.font = 'bold 11px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(lines[0], bx + bw / 2, by + 11);
        ctx.fillText(lines[1], bx + bw / 2, by + 23);
        ctx.textBaseline = 'alphabetic';
      }
    }
  };

  // Treffer-Test einer Zeichnung in Pixelkoordinaten (Block F: Selektion).
  const hitTest = (px: number, py: number): number | null => {
    const chart = chartRef.current, series = candleRef.current;
    if (!chart || !series) return null;
    const X = (t: number) => chart.timeScale().timeToCoordinate(t as UTCTimestamp);
    const Y = (p: number) => series.priceToCoordinate(p);
    const TOL = touchModeRef.current ? 14 : 6; // größere Trefferfläche auf Touch
    for (let i = drawingsRef.current.length - 1; i >= 0; i--) {
      const d = drawingsRef.current[i];
      if (d.kind === 'hline') {
        const yy = Y(d.a.p); if (yy != null && Math.abs(py - yy) <= TOL) return d.id;
        continue;
      }
      if (!d.b) continue;
      const x1 = X(d.a.t), y1 = Y(d.a.p), x2 = X(d.b.t), y2 = Y(d.b.p);
      if (x1 == null || y1 == null || x2 == null || y2 == null) continue;
      if (d.kind === 'rect') {
        const inX = px >= Math.min(x1, x2) - TOL && px <= Math.max(x1, x2) + TOL;
        const inY = py >= Math.min(y1, y2) - TOL && py <= Math.max(y1, y2) + TOL;
        if (inX && inY) return d.id;
      } else {
        // Punkt-zu-Strecke-Distanz
        const dx = x2 - x1, dy = y2 - y1;
        const len2 = dx * dx + dy * dy || 1;
        const tt = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
        const cx = x1 + tt * dx, cy = y1 + tt * dy;
        if (Math.hypot(px - cx, py - cy) <= TOL) return d.id;
      }
    }
    return null;
  };

  // Snap auf nächstes OHLC der Bar unter dem Cursor (Magnet, Block G).
  const snapToBar = (pt: Pt): Pt => {
    const bars = barsRef.current;
    if (!bars.size) return pt;
    let best: Bar | null = null, bestDt = Infinity;
    for (const b of Array.from(bars.values())) {
      const dt = Math.abs(Math.floor(b.openTime / 1000) - pt.t);
      if (dt < bestDt) { bestDt = dt; best = b; }
    }
    if (!best) return pt;
    const cands = [best.o, best.h, best.l, best.c];
    let np = cands[0];
    for (const c of cands) if (Math.abs(c - pt.p) < Math.abs(np - pt.p)) np = c;
    return { t: Math.floor(best.openTime / 1000), p: np };
  };

  // Pixel → Daten-Koordinaten (Zeit/Preis) über die Chart-Inverse.
  const toData = (clientX: number, clientY: number): Pt | null => {
    const canvas = canvasRef.current, chart = chartRef.current, series = candleRef.current;
    if (!canvas || !chart || !series) return null;
    const rect = canvas.getBoundingClientRect();
    const t = chart.timeScale().coordinateToTime(clientX - rect.left);
    const p = series.coordinateToPrice(clientY - rect.top);
    if (p == null) return null;
    const raw: Pt = { t: t == null ? lastTimeRef.current : (t as number), p };
    return magnetRef.current ? snapToBar(raw) : raw;
  };

  // Zoom-Box (Block D): sichtbaren Zeitbereich exakt auf die Box setzen. Die
  // Preisachse hat in lightweight-charts v4 keinen öffentlichen Range-Setter →
  // autoScale fittet die Candles im neuen Fenster (vertikal an die Box angenähert).
  const applyZoomBox = (a: Pt, b: Pt) => {
    const chart = chartRef.current; if (!chart) return;
    const from = Math.min(a.t, b.t), to = Math.max(a.t, b.t);
    if (to > from) {
      try { chart.timeScale().setVisibleRange({ from: from as UTCTimestamp, to: to as UTCTimestamp }); } catch {}
    }
    candleRef.current?.priceScale().applyOptions({ autoScale: true });
    scheduleDraw();
  };

  // Neue Long/Short-Position aus dem Aufziehen (Block C). Entry = Startpreis,
  // Target = Endpreis, Stop initial gespiegelt (1:1) — danach per Handle anpassbar.
  const createPosition = (side: 'long' | 'short', a: Pt, b: Pt) => {
    const entry = a.p;
    const target = b.p;
    const stop = entry - (target - entry);
    let t0 = Math.min(a.t, b.t); let t1 = Math.max(a.t, b.t);
    if (t1 - t0 < tfSec) t1 = t0 + tfSec * 20; // sinnvolle Mindestbreite
    const pos: Position = { id: Date.now(), side, entry, stop, target, t0, t1 };
    positionsRef.current = [...positionsRef.current, pos];
    posSelectedRef.current = pos.id;
    persistPos(); notifySel(); scheduleDraw();
  };

  // Treffer-Test der Positions-Handles (Entry/Stop/Target) in Pixelkoordinaten.
  const hitPosHandle = (px: number, py: number): { id: number; part: 'entry' | 'stop' | 'target' } | null => {
    const chart = chartRef.current, series = candleRef.current;
    if (!chart || !series) return null;
    const X = (t: number) => chart.timeScale().timeToCoordinate(t as UTCTimestamp);
    const Y = (p: number) => series.priceToCoordinate(p);
    const TOL = touchModeRef.current ? 16 : 7; // größere Handle-Trefferfläche auf Touch
    for (let i = positionsRef.current.length - 1; i >= 0; i--) {
      const pos = positionsRef.current[i];
      const xL = X(pos.t0), xR = X(pos.t1);
      const left = Math.min(xL ?? 0, xR ?? 0), right = Math.max(xL ?? 0, xR ?? 0);
      if (px < left - TOL || px > right + TOL) continue;
      const parts: ['entry' | 'stop' | 'target', number][] = [['entry', pos.entry], ['stop', pos.stop], ['target', pos.target]];
      for (const [part, price] of parts) { const yy = Y(price); if (yy != null && Math.abs(py - yy) <= TOL) return { id: pos.id, part }; }
    }
    return null;
  };

  // Touch-Modus (für größere Trefferflächen). Wird bei jedem Pointer-Down gesetzt.
  const touchModeRef = useRef(false);
  // Long-Press → Kontextmenü (Touch-Ersatz für Rechtsklick).
  const longPressRef = useRef<{ timer: number; x: number; y: number } | null>(null);
  const longPressFiredRef = useRef(false);

  // Zeichen-/Tool-Interaktion (Pointer Events: Maus + Touch vereinheitlicht).
  const onDown = (e: React.PointerEvent) => {
    touchModeRef.current = e.pointerType === 'touch';
    if (toolRef.current === 'none') return;
    // Pointer einfangen → Move/Up auch außerhalb des Canvas verfolgen (Touch-Drag).
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* still */ }
    const pt = toData(e.clientX, e.clientY); if (!pt) return;
    if (toolRef.current === 'hline') {
      drawingsRef.current = [...drawingsRef.current, { id: e.timeStamp, kind: 'hline', a: pt }];
      persist(); scheduleDraw(); return;
    }
    // Long/Short: zuerst prüfen, ob ein vorhandenes Handle gegriffen wird (Verschieben).
    if (toolRef.current === 'long' || toolRef.current === 'short') {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const h = hitPosHandle(e.clientX - rect.left, e.clientY - rect.top);
        if (h) {
          const pos = positionsRef.current.find(p => p.id === h.id)!;
          posSelectedRef.current = h.id;
          posDragRef.current = { id: h.id, part: h.part, grabPrice: pt.p, entry0: pos.entry, stop0: pos.stop, target0: pos.target };
          notifySel(); scheduleDraw(); return;
        }
      }
    }
    if (toolRef.current === 'measure') { measureRef.current = null; }
    draftRef.current = { id: e.timeStamp, kind: toolRef.current, a: pt, b: pt };
    scheduleDraw();
  };
  const onMove = (e: React.PointerEvent) => {
    // Aktiver Handle-Drag einer Position hat Vorrang vor dem Aufziehen.
    if (posDragRef.current) {
      const pt = toData(e.clientX, e.clientY); if (!pt) return;
      const dr = posDragRef.current;
      positionsRef.current = positionsRef.current.map(p => {
        if (p.id !== dr.id) return p;
        if (dr.part === 'entry') { const d = pt.p - dr.grabPrice; return { ...p, entry: dr.entry0 + d, stop: dr.stop0 + d, target: dr.target0 + d }; }
        if (dr.part === 'stop') return { ...p, stop: pt.p };
        return { ...p, target: pt.p };
      });
      scheduleDraw(); return;
    }
    if (!draftRef.current) return;
    const pt = toData(e.clientX, e.clientY); if (!pt) return;
    draftRef.current = { ...draftRef.current, b: pt };
    scheduleDraw();
  };
  const onUp = () => {
    if (posDragRef.current) { posDragRef.current = null; persistPos(); scheduleDraw(); return; }
    const d = draftRef.current; if (!d) return;
    draftRef.current = null;
    const moved = d.b && (d.b.t !== d.a.t || d.b.p !== d.a.p);
    if (d.kind === 'zoom') {
      if (moved && d.b) applyZoomBox(d.a, d.b);
    } else if (d.kind === 'long' || d.kind === 'short') {
      if (moved && d.b) createPosition(d.kind, d.a, d.b);
    } else if (d.kind === 'measure') {
      measureRef.current = moved && d.b ? { a: d.a, b: d.b } : null;
    } else if (moved) {
      drawingsRef.current = [...drawingsRef.current, d]; persist();
    }
    scheduleDraw();
  };

  const scheduleDraw = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => { rafRef.current = null; draw(); });
  };

  // --- Chart einmalig erstellen ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const chart = createChart(el, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: '#0a0e1a' }, textColor: '#7a869c', fontSize: 11 },
      grid: { vertLines: { color: '#141a2c' }, horzLines: { color: '#141a2c' } },
      crosshair: {
        mode: CrosshairMode.Normal,
        // Block E: Achsen-Labels am Cursor — Preis (rechts) + Zeit (unten, UTC+2
        // über localization.timeFormatter). Theme-konform eingefärbt.
        vertLine: { color: '#4a5568', width: 1, style: 3, labelVisible: true, labelBackgroundColor: '#8b5cf6' },
        horzLine: { color: '#4a5568', width: 1, style: 3, labelVisible: true, labelBackgroundColor: '#8b5cf6' },
      },
      rightPriceScale: { borderColor: '#1e2539' },
      timeScale: {
        borderColor: '#1e2539', visible: true, timeVisible: true, secondsVisible: false, rightOffset: 6,
        // Granularität je Zoomstufe: Datum beim Rauszoomen (Year/Month/Day-Ticks),
        // Uhrzeit beim Reinzoomen — durchgehend UTC+2.
        tickMarkFormatter: (t: UTCTimestamp, tickType: TickMarkType) =>
          tickType === TickMarkType.Year ? fmtDateUTC2(t as number, true)
            : tickType <= TickMarkType.DayOfMonth ? fmtDateUTC2(t as number)
            : fmtTimeUTC2(t as number),
      },
      localization: { timeFormatter: (t: UTCTimestamp) => fmtTimeUTC2(t as number, true) },
      // Mausrad-Zoom am Cursor zentriert (Block F)
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: true },
    });
    chartRef.current = chart;

    const candle = chart.addCandlestickSeries({
      upColor: '#22c55e', downColor: '#ef4444',
      borderUpColor: '#22c55e', borderDownColor: '#ef4444',
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    });
    candleRef.current = candle;

    const volume = chart.addHistogramSeries({ priceFormat: { type: 'volume' }, priceScaleId: '', color: '#26415f' });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volumeRef.current = volume;

    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !candleRef.current) { setHover(null); return; }
      const cd = param.seriesData.get(candleRef.current) as CandlestickData | undefined;
      const vd = volumeRef.current ? (param.seriesData.get(volumeRef.current) as HistogramData | undefined) : undefined;
      if (cd) setHover({ o: cd.open, h: cd.high, l: cd.low, c: cd.close, v: vd?.value ?? 0, time: param.time as number });
    });

    // Klick (kein Drag): Zeichnung selektieren oder Vertikallinie fixieren (Block F).
    chart.subscribeClick((param) => {
      // Nach einem Long-Press (Touch) kein Klick-Toggle auslösen.
      if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
      if (toolRef.current !== 'none' || !param.point) return;
      const px = param.point.x as number, py = param.point.y as number;
      const hitId = hitTest(px, py);
      if (hitId != null) {
        selectedIdRef.current = hitId; scheduleDraw(); return;
      }
      selectedIdRef.current = null;
      // Vertikallinie an geklickter Zeit fixieren / entfernen.
      const t = param.time != null ? (param.time as number) : null;
      pinnedTimeRef.current = (pinnedTimeRef.current != null) ? null : t;
      scheduleDraw();
    });

    // Overlay an JEDE Chart-Bewegung koppeln → pixelgenau.
    chart.timeScale().subscribeVisibleTimeRangeChange(scheduleDraw);
    chart.timeScale().subscribeVisibleLogicalRangeChange(scheduleDraw);
    chart.subscribeCrosshairMove(scheduleDraw);

    const ro = new ResizeObserver(() => scheduleDraw());
    ro.observe(el);

    // Orientierungswechsel: Overlay nach dem Re-Layout neu fitten (Block D).
    const onOrient = () => { scheduleDraw(); window.setTimeout(scheduleDraw, 250); };
    window.addEventListener('orientationchange', onOrient);

    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', onOrient);
      chart.remove(); chartRef.current = null; candleRef.current = null; volumeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw bei Overlay-/Tick-Änderung
  useEffect(() => { scheduleDraw(); }, [overlay, tickSize]);

  const fitDayNow = () => {
    const chart = chartRef.current; if (!chart) return;
    const todayStart = overlayRef.current?.today?.dayStartSec ?? (Math.floor(Date.now() / 1000 / 86400) * 86400);
    const now = Math.floor(Date.now() / 1000);
    try { chart.timeScale().setVisibleRange({ from: todayStart as UTCTimestamp, to: now as UTCTimestamp }); } catch {}
    candleRef.current?.priceScale().applyOptions({ autoScale: true });
    scheduleDraw();
  };

  // Tastatur-Shortcuts: Alt+R = Reset (Tag fitten), Entf = Auswahl löschen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 'r' || e.key === 'R')) { e.preventDefault(); fitDayNow(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        // Erst ausgewählte Position, sonst ausgewählte Zeichnung löschen.
        if (posSelectedRef.current != null) {
          positionsRef.current = positionsRef.current.filter(p => p.id !== posSelectedRef.current);
          posSelectedRef.current = null; persistPos(); notifySel(); scheduleDraw(); return;
        }
        if (selectedIdRef.current == null) return;
        drawingsRef.current = drawingsRef.current.filter(d => d.id !== selectedIdRef.current);
        selectedIdRef.current = null; persist(); scheduleDraw();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cursor-Preis/-Zeit ableiten und als Kontextmenü an die Seite melden.
  const emitContext = (clientX: number, clientY: number) => {
    if (!onContext) return;
    const chart = chartRef.current, series = candleRef.current, el = containerRef.current;
    if (!chart || !series || !el) return;
    const rect = el.getBoundingClientRect();
    const price = series.coordinateToPrice(clientY - rect.top) ?? null;
    const t = chart.timeScale().coordinateToTime(clientX - rect.left);
    onContext({ clientX, clientY, price, time: t != null ? (t as number) : null });
  };

  // Rechtsklick (Desktop, Block D).
  const onCtxMenu = (e: React.MouseEvent) => {
    if (!onContext) return;
    e.preventDefault();
    emitContext(e.clientX, e.clientY);
  };

  // Long-Press (Touch-Ersatz für Rechtsklick): nur im Cursor-Modus (tool none),
  // damit Zeichen-Gesten nicht gestört werden. Bewegung > 12px bricht ab.
  const clearLongPress = () => {
    if (longPressRef.current) { window.clearTimeout(longPressRef.current.timer); longPressRef.current = null; }
  };
  const onOuterPointerDown = (e: React.PointerEvent) => {
    touchModeRef.current = e.pointerType === 'touch';
    longPressFiredRef.current = false;
    if (e.pointerType !== 'touch' || toolRef.current !== 'none') return;
    const { clientX, clientY } = e;
    clearLongPress();
    const timer = window.setTimeout(() => {
      longPressFiredRef.current = true;
      longPressRef.current = null;
      emitContext(clientX, clientY);
    }, 500);
    longPressRef.current = { timer, x: clientX, y: clientY };
  };
  const onOuterPointerMove = (e: React.PointerEvent) => {
    const lp = longPressRef.current; if (!lp) return;
    if (Math.hypot(e.clientX - lp.x, e.clientY - lp.y) > 12) clearLongPress();
  };
  const onOuterPointerUp = () => clearLongPress();

  // Doppelklick auf die Preisachse → Auto-Scale.
  const onDblClick = (e: React.MouseEvent) => {
    const chart = chartRef.current, el = containerRef.current; if (!chart || !el) return;
    const rect = el.getBoundingClientRect();
    if (e.clientX - rect.left > chart.timeScale().width()) {
      candleRef.current?.priceScale().applyOptions({ autoScale: true });
    }
  };

  // Zeichnungen pro Symbol aus localStorage laden.
  useEffect(() => {
    try { const raw = localStorage.getItem(lsKey); drawingsRef.current = raw ? JSON.parse(raw) : []; }
    catch { drawingsRef.current = []; }
    try { const rp = localStorage.getItem(posLsKey); positionsRef.current = rp ? JSON.parse(rp) : []; }
    catch { positionsRef.current = []; }
    draftRef.current = null; posSelectedRef.current = null; posDragRef.current = null; notifySel();
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // --- Backfill bei Symbol/TF-Wechsel, danach Live ---
  useEffect(() => {
    let cancelled = false;
    readyRef.current = false;
    lastTimeRef.current = 0;
    barsRef.current = new Map();

    (async () => {
      try {
        const r = await fetch(`${API}/market/klines?symbol=${symbol}&interval=${tfCode}&limit=750`);
        if (!r.ok) return;
        const bars: Bar[] = await r.json();
        if (cancelled || !candleRef.current || !volumeRef.current) return;

        const candleData: CandlestickData[] = [];
        const volData: HistogramData[] = [];
        for (const b of bars) {
          const t = Math.floor(b.openTime / 1000) as UTCTimestamp;
          barsRef.current.set(t as number, b);
          candleData.push({ time: t, open: b.o, high: b.h, low: b.l, close: b.c });
          volData.push({ time: t, value: b.v, color: b.c >= b.o ? '#1f6f43' : '#7a2e34' });
        }
        candleRef.current.setData(candleData);
        volumeRef.current.setData(volData);
        chartRef.current?.timeScale().fitContent();

        const last = bars[bars.length - 1];
        if (last) {
          lastTimeRef.current = Math.floor(last.openTime / 1000);
          setLastBar({ o: last.o, h: last.h, l: last.l, c: last.c, v: last.v, time: lastTimeRef.current });
        }
        readyRef.current = true;
        onBars?.(bars, true);
        onReady?.();
        scheduleDraw();
      } catch { /* still */ }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, tfCode]);

  // --- Live-Kline anwenden (hochfrequent, von außen) ---
  useImperativeHandle(ref, () => ({
    applyKline(k: KlineMsg) {
      if (!readyRef.current || k.symbol !== symbol || k.interval !== tfCode) return;
      const candle = candleRef.current, volume = volumeRef.current;
      if (!candle || !volume) return;
      const t = Math.floor(k.openTime / 1000);
      if (t < lastTimeRef.current) return; // alte Bar ignorieren (keine Lücke/Dublette)
      const tt = t as UTCTimestamp;
      candle.update({ time: tt, open: k.o, high: k.h, low: k.l, close: k.c });
      volume.update({ time: tt, value: k.v, color: k.c >= k.o ? '#1f6f43' : '#7a2e34' });
      barsRef.current.set(t, { openTime: k.openTime, o: k.o, h: k.h, l: k.l, c: k.c, v: k.v });
      lastTimeRef.current = t;
      setLastBar({ o: k.o, h: k.h, l: k.l, c: k.c, v: k.v, time: t });
      onBars?.(Array.from(barsRef.current.values()).sort((a, b) => a.openTime - b.openTime), k.confirmed);
      scheduleDraw();
    },
    getChart: () => chartRef.current,
    getCandleSeries: () => candleRef.current,
    redrawOverlay: () => scheduleDraw(),
    clearDrawings: () => { drawingsRef.current = []; selectedIdRef.current = null; measureRef.current = null; persist(); scheduleDraw(); },
    undoDrawing: () => { drawingsRef.current = drawingsRef.current.slice(0, -1); persist(); scheduleDraw(); },
    deleteSelected: () => {
      if (selectedIdRef.current == null) return;
      drawingsRef.current = drawingsRef.current.filter(d => d.id !== selectedIdRef.current);
      selectedIdRef.current = null; persist(); scheduleDraw();
    },
    fitDay: () => {
      const chart = chartRef.current; if (!chart) return;
      const todayStart = overlayRef.current?.today?.dayStartSec ?? (Math.floor(Date.now() / 1000 / 86400) * 86400);
      const now = Math.floor(Date.now() / 1000);
      try { chart.timeScale().setVisibleRange({ from: todayStart as UTCTimestamp, to: now as UTCTimestamp }); } catch {}
      candleRef.current?.priceScale().applyOptions({ autoScale: true });
      scheduleDraw();
    },
    autoScale: () => { candleRef.current?.priceScale().applyOptions({ autoScale: true }); },
    getSelectedPosition: () => {
      const pos = positionsRef.current.find(p => p.id === posSelectedRef.current);
      if (!pos) return null;
      const risk = Math.abs(pos.entry - pos.stop), reward = Math.abs(pos.target - pos.entry);
      return { side: pos.side, symbol, entry: pos.entry, stop: pos.stop, target: pos.target, rr: risk > 0 ? +(reward / risk).toFixed(2) : 0, timeSec: pos.t0 };
    },
    priceAtY: (clientY: number) => {
      const series = candleRef.current, canvas = canvasRef.current;
      if (!series || !canvas) return null;
      const rect = canvas.getBoundingClientRect();
      return series.coordinateToPrice(clientY - rect.top) ?? null;
    },
    snapshot: () => {
      const chart = chartRef.current, overlayCanvas = canvasRef.current;
      if (!chart || !overlayCanvas) return null;
      const base = chart.takeScreenshot(); // Candles + Achsen
      const out = document.createElement('canvas');
      out.width = base.width; out.height = base.height;
      const octx = out.getContext('2d'); if (!octx) return null;
      octx.drawImage(base, 0, 0);
      // Overlay (TPO/Linien/Zeichnungen) deckungsgleich darüber komponieren.
      octx.drawImage(overlayCanvas, 0, 0, base.width, base.height);
      return out.toDataURL('image/png');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [symbol, tfCode, onBars]);

  const info = hover ?? lastBar;
  const up = info ? info.c >= info.o : true;
  const lbl = (k: string, val: string, color?: string) => (
    <span style={{ color: '#5b6678' }}>{k}<span style={{ color: color ?? '#cbd5e1', marginLeft: 4 }}>{val}</span></span>
  );

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', touchAction: 'none' }}
      onDoubleClick={onDblClick} onContextMenu={onCtxMenu}
      onPointerDown={onOuterPointerDown} onPointerMove={onOuterPointerMove}
      onPointerUp={onOuterPointerUp} onPointerCancel={onOuterPointerUp} onPointerLeave={onOuterPointerUp}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      <canvas ref={canvasRef}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        style={{ position: 'absolute', inset: 0, zIndex: 3, touchAction: 'none', pointerEvents: tool === 'none' ? 'none' : 'auto', cursor: tool === 'none' ? 'default' : 'crosshair' }} />
      {info && (
        <div style={{
          position: 'absolute', top: 8, left: 10, zIndex: 5, display: 'flex', gap: 14,
          flexWrap: 'wrap', maxWidth: 'calc(100% - 20px)', rowGap: 2,
          fontSize: 11, fontFamily: 'monospace', pointerEvents: 'none',
          background: '#0a0e1ad0', padding: '4px 10px', borderRadius: 6, border: '1px solid #1e2539',
        }}>
          {lbl('O', fmtPrice(info.o, tickRef.current))}
          {lbl('H', fmtPrice(info.h, tickRef.current))}
          {lbl('L', fmtPrice(info.l, tickRef.current))}
          {lbl('C', fmtPrice(info.c, tickRef.current), up ? '#22c55e' : '#ef4444')}
          {lbl('Vol', info.v.toLocaleString('en-US', { maximumFractionDigits: 0 }))}
          {lbl('', fmtTimeUTC2(info.time, true), '#8b93a7')}
        </div>
      )}
    </div>
  );
});

export default ChartView;
